import React, { useMemo } from "react";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { Card, Flex, Button, Empty, ConfigProvider } from "antd";
import PlusIcon from "@/icons/PlusIcon";
import KanbanCard from "./KanbanCard";
import styles from "./KanbanBoard.module.css";

import { renderStatusTag } from "@/utils/tasks";

// Статичная тема для Ant Design, чтобы не пересоздавать объект на каждый рендер
const KANBAN_CARD_THEME = {
  components: {
    Card: {
      headerBg: "#fff",
      headerHeight: 45,
      headerPadding: 10,
      bodyPadding: 0,
    },
  },
};

// Локально мемоизированная карточка, чтобы не перерисовывать все карточки без изменений
const MemoKanbanCard = React.memo(
  KanbanCard,
  (prev, next) =>
    prev.task === next.task &&
    prev.isDragging === next.isDragging &&
    prev.statuses === next.statuses &&
    prev.priorities === next.priorities &&
    prev.taskTypes === next.taskTypes &&
    prev.isAdmins === next.isAdmins &&
    prev.unit === next.unit &&
    prev.selectable === next.selectable &&
    prev.selected === next.selected &&
    prev.onSelect === next.onSelect
);

/**
 * Компонент колонки Канбан доски
 * @param {Object} status - Объект статуса с uuid и name
 * @param {Array} tasks - Массив задач в этом статусе
 */
const KanbanColumn = ({
  status,
  isDefault,
  statuses = [],
  priorities = [],
  taskTypes = [],
  tasks = [],
  isAdmins = false,
  unit = null,
  onAddNew,
  selectable = false,
  selectedTaskUuids = [],
  onSelectTask,
}) => {
  // Мемоизация заголовка (тег статуса + счётчик)
  const titleContent = useMemo(
    () => (
      <Flex gap={5} align="center">
        <div>
          {renderStatusTag(statuses)({
            value: status.uuid,
            label: status.name,
          })}
        </div>
        <div>{tasks.length}</div>
      </Flex>
    ),
    [statuses, status.uuid, status.name, tasks.length]
  );

  // Стили карточки-колонки неизменны между рендерами
  const cardStyle = useMemo(
    () => ({
      minWidth: "370px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      background: "#f7f7f7",
      border: "none",
    }),
    []
  );

  const cardStylesProp = useMemo(
    () => ({
      title: {
        fontWeight: 400,
        fontSize: 12,
      },
      body: {
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
      },
    }),
    []
  );

  return (
    <div className={styles.kanbanColumn}>
      <ConfigProvider theme={KANBAN_CARD_THEME}>
        <Card
          title={titleContent}
          style={cardStyle}
          styles={cardStylesProp}
          extra={
            isDefault && (
              <Button size="small" icon={<PlusIcon />} onClick={onAddNew} />
            )
          }
        >
          <Droppable droppableId={status.uuid} type="TASK">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`${styles.droppableArea} ${
                  snapshot.isDraggingOver ? styles.isDraggingOver : ""
                }`}
                style={{
                  backgroundColor: snapshot.isDraggingOver
                    ? "#f0f2f5"
                    : "transparent",
                }}
              >
                {tasks.length === 0 ? (
                  <Empty
                    description="Нет задач"
                    style={{ marginBlock: "20px" }}
                  />
                ) : (
                  tasks.map((task, index) => (
                    <Draggable
                      key={task.uuid}
                      draggableId={task.uuid}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            marginBottom: "8px",
                            ...provided.draggableProps.style,
                          }}
                        >
                          <MemoKanbanCard
                            task={task}
                            isDragging={snapshot.isDragging}
                            statuses={statuses}
                            priorities={priorities}
                            taskTypes={taskTypes}
                            isAdmins={isAdmins}
                            unit={unit}
                            selectable={selectable}
                            selected={selectedTaskUuids.includes(task.uuid)}
                            onSelect={onSelectTask}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </Card>
      </ConfigProvider>
    </div>
  );
};

// Кастомная функция сравнения пропсов для мемоизации колонки
const areEqualColumnProps = (prev, next) => {
  if (
    prev.isDefault !== next.isDefault ||
    prev.isAdmins !== next.isAdmins ||
    prev.onAddNew !== next.onAddNew ||
    prev.onSelectTask !== next.onSelectTask ||
    prev.selectable !== next.selectable
  )
    return false;

  // status по ключевым полям
  if (
    prev.status?.uuid !== next.status?.uuid ||
    prev.status?.name !== next.status?.name
  )
    return false;

  // unit, справочники: полагаемся на ссылочную стабильность
  if (
    prev.unit !== next.unit ||
    prev.statuses !== next.statuses ||
    prev.priorities !== next.priorities ||
    prev.taskTypes !== next.taskTypes
  )
    return false;

  if (prev.selectedTaskUuids !== next.selectedTaskUuids) return false;

  // Быстрой проверкой попробуем по ссылке
  if (prev.tasks === next.tasks) return true;

  // Если длина изменилась — точно нужно перерендерить
  if (prev.tasks.length !== next.tasks.length) return false;

  // Шаллоу-сравнение по ссылкам и порядку, чтобы отреагировать на изменения/перемещения
  for (let i = 0; i < prev.tasks.length; i += 1) {
    if (prev.tasks[i] !== next.tasks[i]) return false;
  }
  return true;
};

export default React.memo(KanbanColumn, areEqualColumnProps);
